// Site-wide analytics for sales channels (Meta Pixel, GA4, TikTok Pixel,
// custom snippets). Config is pasted by the admin in Website Editor →
// Tracking and served publicly via GET /api/site (site.tracking).
//
// Safe-by-default: every call is guarded — if a pixel isn't configured
// the call is a silent no-op.

let initialized = false;
let lastPageViewUrl = null;   // dedupe PageView (SPA re-renders, StrictMode)
let lastViewContent = null;   // dedupe ViewContent per product (short window)

function injectScript(src, inline) {
  const s = document.createElement('script');
  if (src) { s.async = true; s.src = src; }
  if (inline) s.innerHTML = inline;
  document.head.appendChild(s);
}

export function initAnalytics(tracking) {
  if (initialized || !tracking) return;
  initialized = true;
  // The pixel bootstrap below fires the initial PageView itself — remember the
  // URL so the SPA route listener can't double-fire it for the same page.
  lastPageViewUrl = window.location.href;

  const metaId = (tracking.meta_pixel_id || '').trim();
  const gaId = (tracking.ga4_id || '').trim();
  const ttId = (tracking.tiktok_pixel_id || '').trim();
  const custom = (tracking.custom_head_html || '').trim();

  // --- Meta (Facebook) Pixel ---
  if (metaId) {
    injectScript(null, `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${metaId}');fbq('track','PageView');`);
  }

  // --- Google Analytics 4 ---
  if (gaId) {
    injectScript(`https://www.googletagmanager.com/gtag/js?id=${gaId}`);
    injectScript(null, `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}window.gtag=gtag;gtag('js',new Date());gtag('config','${gaId}');`);
  }

  // --- TikTok Pixel ---
  if (ttId) {
    injectScript(null, `!function(w,d,t){w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e},ttq.load=function(e,n){var i="https://analytics.tiktok.com/i18n/pixel/events.js";ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=i,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};var o=document.createElement("script");o.type="text/javascript",o.async=!0,o.src=i+"?sdkid="+e+"&lib="+t;var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(o,a)}(window,document,'ttq');ttq.load('${ttId}');ttq.page();`);
  }

  // --- Custom snippets (any channel: Pinterest, Snap, Bing, GTM…) ---
  if (custom) {
    try {
      const holder = document.createElement('div');
      holder.innerHTML = custom;
      holder.querySelectorAll('script').forEach(node => {
        injectScript(node.src || null, node.src ? null : node.innerHTML);
      });
    } catch { /* malformed snippet — skip */ }
  }
}

// ---------------- standard e-commerce events ----------------
// Meta catalog matching: retailer IDs in the Meta Commerce catalog equal our
// product SKU (falls back to product id) — same as the catalog CSV `id` column.
const catalogId = (product) => product?.sku || product?.id;

export function trackPageView() {
  // The pixel bootstrap fires the first PageView itself at init time —
  // ignore any calls before that, and one PageView per URL after.
  if (!initialized) return;
  if (window.location.href === lastPageViewUrl) return;
  lastPageViewUrl = window.location.href;
  try { window.fbq && window.fbq('track', 'PageView'); } catch {}
  try { window.ttq && window.ttq.page(); } catch {}
  // GA4 tracks page_view automatically on config; SPA route changes:
  try { window.gtag && window.gtag('event', 'page_view', { page_location: window.location.href }); } catch {}
}

export function trackViewContent(product, price = null) {
  const value = Number(price ?? product?.price ?? 0);
  const cid = catalogId(product);
  // Dedupe: same product within 3s (double-mounted effects, fast re-renders)
  const now = Date.now();
  if (lastViewContent && lastViewContent.cid === cid && now - lastViewContent.t < 3000) return;
  lastViewContent = { cid, t: now };
  try {
    window.fbq && window.fbq('track', 'ViewContent', {
      content_ids: [cid], content_name: product?.name,
      content_type: 'product', content_category: product?.category,
      value, currency: 'USD',
    });
  } catch {}
  try {
    window.gtag && window.gtag('event', 'view_item', {
      currency: 'USD', value,
      items: [{ item_id: cid, item_name: product?.name }],
    });
  } catch {}
  try {
    window.ttq && window.ttq.track('ViewContent', {
      content_id: cid, content_name: product?.name, value, currency: 'USD',
    });
  } catch {}
}

export function trackSearch(query, resultCount = null) {
  if (!query) return;
  try {
    window.fbq && window.fbq('track', 'Search', {
      search_string: query,
      ...(resultCount != null ? { content_category: `${resultCount} results` } : {}),
    });
  } catch {}
  try { window.gtag && window.gtag('event', 'search', { search_term: query }); } catch {}
  try { window.ttq && window.ttq.track('Search', { query }); } catch {}
}

export function trackAddToCart(product, qty = 1, price = null) {
  const value = Number(price ?? product?.price ?? 0) * qty;
  const cid = catalogId(product);
  try {
    window.fbq && window.fbq('track', 'AddToCart', {
      content_ids: [cid], content_name: product?.name,
      content_type: 'product', value, currency: 'USD',
    });
  } catch {}
  try {
    window.gtag && window.gtag('event', 'add_to_cart', {
      currency: 'USD', value,
      items: [{ item_id: cid, item_name: product?.name, quantity: qty }],
    });
  } catch {}
  try {
    window.ttq && window.ttq.track('AddToCart', {
      content_id: cid, content_name: product?.name,
      quantity: qty, value, currency: 'USD',
    });
  } catch {}
}

export function trackInitiateCheckout(value, numItems, contentIds = []) {
  try {
    window.fbq && window.fbq('track', 'InitiateCheckout', {
      value, currency: 'USD', num_items: numItems,
      ...(contentIds.length ? { content_ids: contentIds, content_type: 'product' } : {}),
    });
  } catch {}
  try { window.gtag && window.gtag('event', 'begin_checkout', { currency: 'USD', value }); } catch {}
  try { window.ttq && window.ttq.track('InitiateCheckout', { value, currency: 'USD' }); } catch {}
}

// Fired once, at the moment the shopper hands off to Stripe's secure payment
// page (the closest "payment info" step in a Stripe-hosted checkout).
export function trackAddPaymentInfo(value, numItems, contentIds = []) {
  try {
    window.fbq && window.fbq('track', 'AddPaymentInfo', {
      value, currency: 'USD', num_items: numItems,
      ...(contentIds.length ? { content_ids: contentIds, content_type: 'product' } : {}),
    });
  } catch {}
  try { window.gtag && window.gtag('event', 'add_payment_info', { currency: 'USD', value }); } catch {}
  try { window.ttq && window.ttq.track('AddPaymentInfo', { value, currency: 'USD' }); } catch {}
}

export function trackPurchase(value, orderRef, currency = 'USD', contentIds = [], numItems = 0) {
  // Guard against double-firing: refreshes, back/forward navigation and
  // repeat visits to the confirmation page all no-op after the first fire.
  // (localStorage survives tab restores, unlike sessionStorage.)
  try {
    const key = `rp_purchase_${orderRef || value}`;
    if (localStorage.getItem(key) || sessionStorage.getItem(key)) return;
    localStorage.setItem(key, '1');
    sessionStorage.setItem(key, '1');
  } catch {}
  const cur = (currency || 'USD').toUpperCase();
  try {
    window.fbq && window.fbq('track', 'Purchase', {
      value, currency: cur,
      ...(contentIds.length ? { content_ids: contentIds, content_type: 'product' } : {}),
      ...(numItems ? { num_items: numItems } : {}),
    });
  } catch {}
  try { window.gtag && window.gtag('event', 'purchase', { currency: cur, value, transaction_id: orderRef }); } catch {}
  try { window.ttq && window.ttq.track('CompletePayment', { value, currency: cur }); } catch {}
}
